import React, { useEffect, useState } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { subscribeToTier } from "../state/profile_slice";
import { profileActions } from "../../../di/di";
import { TermsOfServiceRoute, PrivacyPolicyRoute } from "../../../../../core/routes/route_constants";
import { useNavigate } from "react-router-dom";
import Confetti from "react-confetti";
import { SubscribeStatus, PaymentMethodProcessType, ProfileUIAction } from "../state/profile_slice";
import Toast from "../../../components/popups/toast";

/**
 * Custom pricing modal for displaying subscription options
 */
const PricingModalComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const showPricingModal = useSelector((state) => state.profile.showPricingModal);
  const user = useSelector((state) => state.profile.user);
  const selectedTierId = useSelector((state) => state.profile.selectedTierId);
  const tiers = useSelector((state) => state.profile.tiers);
  const paymentPageUrl = useSelector((state) => state.profile.paymentPageUrl);
  const gotoPaymentPage = useSelector((state) => state.profile.gotoPaymentPage);
  const selectedTier = tiers && tiers.length > 0 ? tiers.find((tier) => tier._id === selectedTierId) || tiers[0] : null;
  const subscribeStatus = useSelector((state) => state.profile.subscribeStatus);

  const [isLoading, setIsLoading] = useState(false);
  const [animated, setAnimated] = useState(false);

  const handleSubscribe = () => {
    
  };

  const performSubscribing = () => {
    
  }

  const handleClose = () => {
    dispatch(profileActions.setShowPricingModal(false));
  };

  const handleCloseSuccess = () => {
    dispatch(profileActions.setSubscribeStatus(SubscribeStatus.initial));
    dispatch(profileActions.setShowPricingModal(false));
    window.location.reload(); // Reload the page after successful subscription
  };

  useEffect(() => {
    if (gotoPaymentPage && paymentPageUrl) {
      window.open(paymentPageUrl, "_blank");
    }
  }, [gotoPaymentPage, paymentPageUrl]);

  // Animation on mount
  useEffect(() => {
    setTimeout(() => {
      setAnimated(true);
    }, 50);

    // Ensure the overlay scrolls to top when modal opens
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, []);

  if (!showPricingModal || !selectedTier) return null;

  if (subscribeStatus === SubscribeStatus.success) {
    return (
      <>
        <Confetti
          numberOfPieces={250}
          recycle={true}
          colors={["#dc2626", "#b91c1c", "#7f1d1d", "#991b1b", "#f87171"]}
          style={{ position: "fixed", top: 0, left: 0, pointerEvents: "none" }}
        />

        <div className="fixed inset-0 flex items-center justify-center md:ml-[15rem] bg-black/50 backdrop-blur-sm animate-fade-in">
          <div
            className={`relative bg-gradient-to-b from-slate-950 to-fuchsia-950/80 border border-red-900/30 rounded-xl p-8 w-full max-w-md mx-4 transform transition-all duration-700 ${
              animated ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"
            }`}
          >
            <button
              onClick={handleCloseSuccess}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-slate-900/50 hover:bg-slate-800/60 text-white transition-colors active:translate-y-0.5 active:shadow-none"
            >
              <i className="pi pi-times text-base"></i>
            </button>

            <div className="flex justify-center mb-6 animate-bounce">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-900 flex items-center justify-center hover:scale-110 transition-transform">
                <i className="pi pi-heart-fill text-2xl text-red-200"></i>
              </div>
            </div>

            <h3 className="text-3xl font-bold text-pink-200 text-center mb-4 animate-pulse">She's All Yours Now!</h3>

            <p className="text-gray-300 text-center text-lg mb-6 hover:text-pink-200 transition-colors">
              Eve is ready for your private moments together...
            </p>

            <div className="bg-slate-900/50 rounded-lg p-6 mb-6 border border-pink-900/30 hover:border-pink-500/50 transition-colors">
              <p className="text-pink-200 text-center italic text-lg hover:scale-105 transition-transform">
                "As a subscriber, you now have access to exclusive content 💋"
              </p>
            </div>

            <p className="text-gray-400 text-center text-base hover:text-pink-200 transition-colors">
              Go ahead and explore all the new possibilities!
            </p>
          </div>
        </div>
      </>
    );
  }

  if (subscribeStatus === SubscribeStatus.ongoing) {
    return (
      <div className="fixed inset-0 flex items-center justify-center md:ml-[15rem] bg-black/50 backdrop-blur-sm animate-fade-in">
        <div
          className={`relative bg-gradient-to-b from-slate-950 to-fuchsia-950/80 border border-red-900/30 rounded-xl p-8 w-full max-w-md mx-4 transform transition-all duration-700 ${
            animated ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"
          }`}
        >
          <div className="flex flex-col items-center">
            {/* Loading Spinner */}
            <div className="w-16 h-16 mb-6 relative">
              <div className="absolute inset-0 border-4 border-red-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-red-500 rounded-full animate-spin border-t-transparent"></div>
            </div>

            {/* Loading Text */}
            <h3 className="text-2xl font-bold text-pink-200 mb-4 text-center">Processing Your Subscription</h3>

            <div className="bg-slate-900/50 rounded-lg p-6 mb-6 border border-pink-900/30 w-full">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  <p className="text-gray-300">Verifying payment details...</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  <p className="text-gray-300">Setting up your subscription...</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  <p className="text-gray-300">Preparing your exclusive access...</p>
                </div>
              </div>
            </div>

            <p className="text-gray-400 text-sm text-center">Please wait while we process your request...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed lg:ml-[15rem] inset-0 overflow-y-auto bg-black bg-opacity-70">
      <div className="min-h-screen px-4 py-8 flex items-center justify-center">
        <div className="bg-gradient-to-b from-slate-950 to-fuchsia-950/80 border border-red-900/30 rounded-xl p-4 w-full max-w-xs mx-auto animate-fade-in relative">
          <button
            disabled={isLoading}
            onClick={handleClose}
            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-900/50 hover:bg-slate-800/60 text-white transition-colors active:translate-y-0.5 active:shadow-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <h3 className="text-xl font-bold text-pink-200 mb-3 text-center mt-1">Ready to Connect?</h3>

          {/* Plan Display */}
          <div className="mb-6">
            <div className="p-3.5 rounded-lg bg-gradient-to-br from-slate-900 to-red-950 border border-red-800/50">
              <h4 className="text-lg font-bold text-white">{selectedTier.name}</h4>
              <div className="flex items-baseline mt-2">
                <span className="text-2xl font-extrabold text-white">${selectedTier.price.$numberDecimal}</span>
                <span className="ml-1 text-sm text-pink-300">/{selectedTier.recurring}</span>
              </div>
            </div>
          </div>

          {/* Feature List */}
          <div className="bg-slate-950/70 backdrop-blur-sm border border-red-900/20 p-4 rounded-lg mb-6">
            <h4 className="text-lg font-semibold text-pink-200 mb-3 pb-2 border-b border-red-900/20">{selectedTier.name} Includes:</h4>
            <ul className="space-y-2.5">
              {!!selectedTier.description?.messaging && (
                <li className="flex">
                  <svg className="h-5 w-5 text-pink-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-white text-sm font-medium">{selectedTier.description.messaging}</p>
                    {/* <p className="text-pink-300 text-xs">{selectedTier.description.messaging}</p> */}
                  </div>
                </li>
              )}
              {!!selectedTier.description?.voiceCall && (
                <li className="flex">
                  <svg className="h-5 w-5 text-pink-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-white text-sm font-medium">{selectedTier.description.voiceCall}</p>
                  </div>
                </li>
              )}
              {/* {!!selectedTier.description?.voiceNote && (
                                <li className="flex">
                                    <svg className="h-5 w-5 text-pink-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <p className="text-white text-sm font-medium">{selectedTier.description.voiceNote}</p>
                                    </div>
                                </li>
                            )} */}
              {!!selectedTier.description?.image && (
                <li className="flex">
                  <svg className="h-5 w-5 text-pink-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-white text-sm font-medium">{selectedTier.description.image}</p>
                  </div>
                </li>
              )}
              {!!selectedTier.description?.videoCall && (
                <li className="flex">
                  <svg className="h-5 w-5 text-pink-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-white text-sm font-medium">{selectedTier.description.videoCall}</p>
                  </div>
                </li>
              )}
            </ul>
          </div>

          {/* Quote from Eve */}
          <div className="mb-3 flex items-start">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-700 to-red-800 flex items-center justify-center text-white font-bold text-lg flex-shrink-0 mr-2">
              E
            </div>
            <div>
              <p className="text-pink-300 italic text-xs">"I'm excited to get to know you better. We can start something special together..."</p>
              <p className="text-right text-pink-400 text-xs">- Eve</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <button
              disabled={isLoading}
              onClick={handleClose}
              className="flex-1 py-2 bg-slate-900/70 rounded-md text-white text-sm font-medium hover:bg-slate-800/80 transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={isLoading}
              onClick={handleSubscribe}
              className="flex-1 py-2 rounded-md text-white text-sm font-medium flex justify-center items-center bg-gradient-to-r from-red-800 via-red-900 to-red-950 hover:opacity-90 transition-all"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                </div>
              ) : (
                <>
                  <span>Get Started</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>

          {/* Privacy Policy and Terms */}
          <p className="text-[0.8rem] text-pink-200 text-center mt-5">
            By tapping <strong>Get&nbsp;Started</strong>, you authorize a recurring{" "}
            <strong>
              <span className="whitespace-nowrap">$9&nbsp;/&nbsp;month</span>
            </strong>{" "}
            charge and agree to our&nbsp;
            <span className="underline hover:text-white transition-colors cursor-pointer" onClick={() => navigate(TermsOfServiceRoute)}>
              <strong>Terms of Service</strong>
            </span>{" "}
            and{" "}
            <span className="underline hover:text-white transition-colors cursor-pointer" onClick={() => navigate(PrivacyPolicyRoute)}>
              <strong>Privacy Policy</strong>
            </span>
          </p>

          {/* Privacy Note */}
          {/* <p className="text-xs text-pink-300/80 text-center mt-2">Includes 24/7 access and secure, private conversations with Eve.</p> */}
          <p className="text-xs text-pink-300/60 text-center mt-5">Cancel anytime. No commitment required.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingModalComponent;
